import { Avatar, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack, type Theme } from "@mui/material";
import { isAcceptableImageUploadContentType } from "@src/appV2/lib/files/utils";
import { type ChatMessage, parseSmoochFileMessage } from "@src/appV2/support/hooks/useSupportChat";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { formatInTimeZone } from "date-fns-tz";

import { LinkifiedText } from "./LinkifiedText";

function formatTimestamp(epochSeconds: number, timezone: string): string {
  return formatInTimeZone(new Date(epochSeconds * 1000), timezone, "h:mm a");
}

function isLastMessageFromParticipant(props: {
  messages: ChatMessage[];
  currentIndex: number;
}): boolean {
  const { messages, currentIndex } = props;
  const currentMessage = messages[currentIndex];
  const nextMessage = messages[currentIndex + 1];

  if (!isDefined(nextMessage)) {
    return true;
  }

  return currentMessage.role !== nextMessage.role;
}

interface SupportChatMessageListProps {
  messages: ChatMessage[];
  timezone: string;
}

export function SupportChatMessageList(props: SupportChatMessageListProps) {
  const { messages, timezone } = props;

  const firstMessageTimestamp = messages[0]?.received;

  return (
    <Stack spacing={4} sx={{ width: "100%" }}>
      {isDefined(firstMessageTimestamp) && (
        <Text variant="caption" color="text.secondary" sx={{ textAlign: "center", width: "100%" }}>
          {formatTimestamp(firstMessageTimestamp, timezone)}
        </Text>
      )}
      {messages.map((message, index) => {
        const { role, avatarUrl, text, id, displayName, received } = message;
        const isUser = role === "user";
        const fileMessage = parseSmoochFileMessage(message);
        const showAvatar = !isUser && isDefined(avatarUrl);
        const showTimestamp =
          isLastMessageFromParticipant({ messages, currentIndex: index }) && isDefined(received);

        return (
          <Box
            key={id}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: isUser ? "flex-end" : "flex-start",
              width: "100%",
            }}
          >
            {!isUser && (
              <Text
                variant="caption"
                color="text.secondary"
                sx={{ marginBottom: 1, marginLeft: showAvatar ? "40px" : 0 }}
              >
                {displayName}
              </Text>
            )}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-end",
                gap: 2,
                maxWidth: "80%",
              }}
            >
              {showAvatar && <Avatar src={avatarUrl} alt={displayName} size="small" />}
              <Box
                sx={(theme: Theme) => ({
                  backgroundColor: isUser
                    ? theme.palette?.intent?.neutral?.background
                    : theme.palette.background.paper,
                  color: isUser ? theme.palette?.intent?.neutral?.text : theme.palette.text.primary,
                  borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                  paddingX: 4,
                  paddingY: 3,
                  boxShadow: theme.shadows[1],
                  wordBreak: "break-word",
                })}
              >
                <LinkifiedText text={text} />
                {isDefined(fileMessage) &&
                  isAcceptableImageUploadContentType(fileMessage.mediaType) && (
                    <Box
                      component="img"
                      src={fileMessage.mediaUrl}
                      alt={text}
                      sx={{ maxWidth: "100%", borderRadius: 2, marginTop: 2 }}
                    />
                  )}
              </Box>
            </Box>
            {showTimestamp && (
              <Text
                variant="caption"
                color="text.secondary"
                sx={{ marginTop: 1, marginLeft: showAvatar ? "40px" : 0 }}
              >
                {formatTimestamp(received, timezone)}
              </Text>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}
